"use client"

import { toast } from "react-hot-toast"
import type { Application } from "../types/application.types"
import type { InterviewDetailsResponse } from "../../services/interviewapi"
import type { SearchableSelectOption } from "../ui/searchable-select"

export const APPLICATION_STATUS_FILTER_OPTIONS: SearchableSelectOption[] = [
  { value: "all", label: "All Status" },
  { value: "Applied", label: "Applied" },
  { value: "Interview Scheduled", label: "Scheduled Interview" },
  { value: "Interview Rescheduled", label: "Rescheduled Interview" },
  { value: "Interview Today", label: "Today's Interviews" },
  { value: "Multi-Level Interview", label: "Multi-Level Interviews" },
  { value: "Selected", label: "Selected" },
  { value: "Offered", label: "Offered" },
  { value: "Joined", label: "Joined" },
  { value: "Rejected by Company", label: "Rejected by Company" },
  { value: "Interview Attended", label: "Interview Attended" },
  { value: "Interview Not Attended", label: "Interview Not Attended" },
  { value: "Not Joined", label: "Not Joined" },
  { value: "Not Selected", label: "Not Selected" },
]

export const INTERVIEW_TODAY_STATUS_FILTER = "Interview Today"

export const isInterviewTodayStatusFilter = (status: string) =>
  status === INTERVIEW_TODAY_STATUS_FILTER

// Utility to format time to 12-hour format with AM/PM
export function formatTimeTo12Hour(time: string): string {
  if (!time) return ""
  const [hourStr, minuteStr] = time.split(":")
  let hour = Number.parseInt(hourStr, 10)
  const minute = Number.parseInt(minuteStr, 10)
  const ampm = hour >= 12 ? "PM" : "AM"
  hour = hour % 12 || 12
  return `${hour}:${minute.toString().padStart(2, "0")} ${ampm}`
}

// Utility function to get a display name for nextActionBy (user id, username, or role)
export function getNextActionByName(nextActionBy: string | undefined): string {
  if (!nextActionBy) return "N/A"
  // Since we no longer fetch all users, just return the string as is
  return nextActionBy
}

// Get status badge color
export const getStatusBadgeColor = (status: string) => {
  switch (status.toLowerCase()) {
    case "applied":
      return "bg-blue-50 text-blue-700 border-blue-200"
    case "under review":
    case "screening pending":
      return "bg-yellow-50 text-yellow-700 border-yellow-200"
    case "screening completed":
      return "bg-amber-50 text-amber-700 border-amber-200"
    case "interview scheduled":
    case "interview rescheduled":
    case "interview attended":
      return "bg-purple-50 text-purple-700 border-purple-200"
    case "interview completed":
      return "bg-indigo-50 text-indigo-700 border-indigo-200"
    case "not attended":
    case "rejected by company":
    case "rejected by candidate":
    case "interview not attended":
    case "not selected":
    case "offer rejected":
      return "bg-red-50 text-red-700 border-red-200"
    case "offered":
    case "offer accepted":
      return "bg-emerald-50 text-emerald-700 border-emerald-200"
    case "joined":
    case "tenure approved":
      return "bg-green-50 text-green-700 border-green-200"
    case "tenure pending":
    case "on hold":
      return "bg-orange-50 text-orange-700 border-orange-200"
    case "tenure rejected":
    case "closed - joined other position":
      return "bg-gray-50 text-gray-700 border-gray-200"
    // Legacy status mappings (for backward compatibility)
    case "screening":
      return "bg-yellow-50 text-yellow-700 border-yellow-200"
    case "interview":
      return "bg-purple-50 text-purple-700 border-purple-200"
    case "selected":
      return "bg-green-50 text-green-700 border-green-200"
    case "rejected":
      return "bg-red-50 text-red-700 border-red-200"
    case "scheduled":
      return "bg-purple-50 text-purple-700 border-purple-200"
    case "completed":
      return "bg-green-50 text-green-700 border-green-200"
    case "qualified":
      return "bg-green-50 text-green-700 border-green-200"
    case "rejected":
      return "bg-red-50 text-red-700 border-red-200"
    case "shortlisted":
      return "bg-green-50 text-green-700 border-green-200"
    default:
      return "bg-gray-50 text-gray-700 border-gray-200"
  }
}

export const NOT_SELECTED_ONLY_STATUS_CHANGE_STATUSES = ["Interview Attended"] as const

export const isNotSelectedOnlyStatusChange = (currentStatus: string) =>
  currentStatus === "Interview Attended"

const DEFAULT_STATUS_CHANGE_OPTIONS = [
  { value: "Joined", label: "Joined" },
  { value: "Offered", label: "Offered" },
  { value: "Reassign Company", label: "Reassign Company" },
  { value: "Not Joined", label: "Not Joined" },
]

const NOT_SELECTED_OPTION = { value: "Not Selected", label: "Not Selected" }

export const getStatusChangeOptions = (currentStatus: string) => {
  if (isNotSelectedOnlyStatusChange(currentStatus)) {
    return [NOT_SELECTED_OPTION]
  }
  if (currentStatus === "Selected") {
    return [...DEFAULT_STATUS_CHANGE_OPTIONS, NOT_SELECTED_OPTION]
  }
  return DEFAULT_STATUS_CHANGE_OPTIONS
}

export const getDefaultStatusChangeValue = (currentStatus: string) => {
  if (isNotSelectedOnlyStatusChange(currentStatus)) return "Not Selected"
  return "Joined"
}

// Get the last round (editable round)
export const getLastRound = (rounds: any[]) => {
  if (!rounds || rounds.length === 0) return null
  return rounds[rounds.length - 1]
}

// Check if round can be scheduled
export const canScheduleRound = (round: any) => {
  return round && (round.status === "Not Scheduled" || !round.scheduledDate)
}

// Check if round can be rescheduled
export const canRescheduleRound = (round: any) => {
  return round && round.scheduledDate && round.status === "Scheduled"
}

// Check if round can have result submitted
export const canSubmitResult = (round: any) => {
  return round && round.status === "Scheduled" && round.status !== "Completed"
}

// Check if we can add a new round (when current round is completed but not final)
export const canAddNewRound = (round: any, isLastRound: boolean) => {
  return (
    isLastRound &&
    round.status === "Completed" &&
    round.feedback &&
    round.feedback.length > 0 &&
    round.feedback.some((fb: any) => fb.recommendation === "Qualified")
  )
}

// Check if a round is the final round
export const isFinalRound = (round: any, allRounds: any[]) => {
  // Check if this was marked as final round in feedback
  const markedAsFinal = round.feedback?.some((fb: any) => fb.isFinalRound) || false
  
  // Check if it's the last round in sequence
  const isLastRoundInSequence = 
    allRounds.length > 0 && allRounds.findIndex((r) => r.id === round.id) === allRounds.length - 1
  
  return markedAsFinal || isLastRoundInSequence
}

// Utility function to validate email addresses
export function isValidEmail(email: string): boolean {
  // Simple email regex for validation
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
}

// Utility function to validate time range
export function validateTimeRange(startTime: string, endTime: string): { isValid: boolean; message?: string } {
  if (!startTime || !endTime) {
    return { isValid: true } // Skip validation if times are empty
  }

  try {
    const [startHour, startMinute] = startTime.split(":").map(Number)
    const [endHour, endMinute] = endTime.split(":").map(Number)

    // Check for valid time format
    if (isNaN(startHour) || isNaN(startMinute) || isNaN(endHour) || isNaN(endMinute)) {
      return { isValid: false, message: "Invalid time format" }
    }

    const startTimeInMinutes = startHour * 60 + startMinute
    const endTimeInMinutes = endHour * 60 + endMinute

    if (startTimeInMinutes >= endTimeInMinutes) {
      return {
        isValid: false,
        // message: "End time must be after start time. Please adjust the timing.",
      }
    }

    const durationMinutes = endTimeInMinutes - startTimeInMinutes

    // Check maximum duration (8 hours)
    if (durationMinutes > 480) {
      return {
        isValid: false,
        message: "Interview duration cannot exceed 8 hours. Please adjust the timing.",
      }
    }

    return { isValid: true }
  } catch (error) {
    return { isValid: false, message: "Invalid time format" }
  }
}

// Utility function to get the next round name
export const getNextRoundName = (currentRound: any, allRounds: any[]) => {
  // Extract round number from round name (e.g., "Round 1" -> 1, "L3 Interview" -> 3)
  const currentRoundName = currentRound.roundName || ""

  // Try to extract number from various round name formats
  let currentRoundNumber = 1

  // Check for "Round X" format
  const roundMatch = currentRoundName.match(/Round\s+(\d+)/i)
  if (roundMatch) {
    currentRoundNumber = Number.parseInt(roundMatch[1], 10)
  }
  // Check for "LX Interview" format
  else if (currentRoundName.match(/L(\d+)/i)) {
    const lMatch = currentRoundName.match(/L(\d+)/i)
    if (lMatch) {
      currentRoundNumber = Number.parseInt(lMatch[1], 10)
    }
  }
  // Check for "RX" format
  else if (currentRoundName.match(/R(\d+)/i)) {
    const rMatch = currentRoundName.match(/R(\d+)/i)
    if (rMatch) {
      currentRoundNumber = Number.parseInt(rMatch[1], 10)
    }
  }
  // If no number found, determine based on position in array
  else {
    const roundIndex = allRounds.findIndex((r) => r.id === currentRound.id)
    currentRoundNumber = roundIndex + 1
  }

  // Check if this was marked as final round or if it's the last round
  const isLastRoundInSequence =
    allRounds.length > 0 && allRounds.findIndex((r) => r.id === currentRound.id) === allRounds.length - 1

  // Check if the round was marked as final through feedback
  const isFinalRound = currentRound.feedback?.some((fb: any) => fb.isFinalRound) || false

  if (isFinalRound || isLastRoundInSequence) {
    return "Final Selection"
  }

  const nextRoundNumber = currentRoundNumber + 1
  return `R${nextRoundNumber}`
}

export function getInterviewDate(app: Application, interviewDetails?: InterviewDetailsResponse | null): string {
  // If interview details exist, try to get the scheduled date of the latest round
  if (app.interviewId && interviewDetails && interviewDetails.data?.interview?.rounds?.length > 0) {
    const rounds = interviewDetails.data.interview.rounds
    const lastRound = rounds[rounds.length - 1]
    if (lastRound?.scheduledDate) {
      return new Date(lastRound.scheduledDate).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    }
  }

  // Fallback: show appliedOn date
  if (app.appliedOn) {
    return new Date(app.appliedOn).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    })
  }

  return "N/A"
}

// Modern confirmation helper function to replace window.confirm
export const showConfirmation = (
  title: string,
  message: string,
  onConfirm: () => void,
  options: {
    confirmText?: string
    cancelText?: string
    type?: "danger" | "warning" | "info"
    onCancel?: () => void
  } = {},
) => {
  const { confirmText = "Confirm", cancelText = "Cancel", type = "warning", onCancel } = options

  // Show confirmation toast with action buttons
  toast(
    (t) => (
      <div className="flex flex-col gap-3 max-w-md">
        <div className="flex items-start gap-2">
          <div
            className={`w-5 h-5 rounded-full flex items-center justify-center text-white text-xs font-bold mt-0.5 ${
              type === "danger" ? "bg-red-500" : type === "warning" ? "bg-yellow-500" : "bg-blue-500"
            }`}
          >
            {type === "danger" ? "!" : type === "warning" ? "⚠" : "i"}
          </div>
          <div className="flex-1">
            <div className="font-semibold text-gray-900 mb-1">{title}</div>
            <div className="text-sm text-gray-600 whitespace-pre-line">{message}</div>
          </div>
        </div>
        <div className="flex gap-2 justify-end">
          <button
            onClick={() => {
              toast.dismiss(t.id)
              onCancel?.()
            }}
            className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
          >
            {cancelText}
          </button>
          <button
            onClick={() => {
              toast.dismiss(t.id)
              onConfirm()
            }}
            className={`px-3 py-1.5 text-sm font-medium text-white rounded-md transition-colors ${
              type === "danger"
                ? "bg-red-600 hover:bg-red-700"
                : type === "warning"
                  ? "bg-yellow-600 hover:bg-yellow-700"
                  : "bg-blue-600 hover:bg-blue-700"
            }`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    ),
    {
      duration: Number.POSITIVE_INFINITY, // Keep open until user clicks
      style: {
        background: "white",
        border: `2px solid ${type === "danger" ? "#ef4444" : type === "warning" ? "#f59e0b" : "#3b82f6"}`,
        borderRadius: "12px",
        padding: "16px",
        boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
      },
    },
  )
}

export const getInterviewOwnershipConflict = (
  application: Application | null,
  currentUserId?: string
): { creatorName: string } | null => {
  if (!application || !currentUserId) return null

  const creatorId = application.sourcedBy?.id
  if (!creatorId || creatorId === currentUserId) return null

  return {
    creatorName: application.sourcedBy?.username || "Unknown",
  }
}
